import React, { createContext, useContext, useCallback, useEffect, useRef, useState } from 'react';
import { toast } from '../components/ui/sonner';
import { incrementPlayCount } from '../services/api';
import { getResumePosition, saveResumePosition } from '../utils/resume';
import { trackRecordingVisit } from '../utils/history';
import { getOfflineAudioUrl } from '../utils/offlineAudio';
import { getLocalAudio, prefetchLocalAudio, retainLocalAudio } from '../utils/localAudio';

const PlayerContext = createContext(null);
export const usePlayer = () => useContext(PlayerContext);

const PREFS_KEY = 'vaad_player_prefs_v1';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const SPEED_OPTIONS = [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2];

// Full-file local copy starts after this much listening (or at once when the
// phone is locked sooner) — protects real listeners, spares data for samplers.
const LOCAL_START_DELAY_MS = 15000;
// The background full-file copy only pulls data while the live stream has at
// least this many seconds buffered ahead. Otherwise the download saturates a
// phone's connection and playback can't buffer ("too busy downloading to listen").
const DOWNLOAD_MIN_AHEAD = 25;
const isBlobSrc = (el) => !!el && (el.currentSrc || el.src || '').startsWith('blob:');
// Seconds of audio the element has buffered past the playhead.
const bufferedAhead = (el) => {
  try {
    const b = el.buffered;
    const t = el.currentTime || 0;
    for (let i = b.length - 1; i >= 0; i -= 1) {
      if (b.start(i) <= t + 0.5 && b.end(i) >= t) return b.end(i) - t;
    }
  } catch { /* ignore */ }
  return 0;
};

export const formatTime = (secs) => {
  if (!Number.isFinite(secs) || secs < 0) return '--:--';
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = Math.floor(secs % 60);
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  return `${m}:${String(s).padStart(2, '0')}`;
};

const loadPrefs = () => {
  try {
    return JSON.parse(localStorage.getItem(PREFS_KEY)) || {};
  } catch {
    return {};
  }
};

// Honour the master `Settings → Auto-play next` toggle if the user set one.
// This lets the Settings page own that preference so the player itself
// doesn't need a per-recording button.
const loadAutoNextFromSettings = () => {
  try {
    const raw = localStorage.getItem('vaad_settings_v1');
    if (!raw) return null;
    const s = JSON.parse(raw);
    return typeof s?.autoplayNext === 'boolean' ? s.autoplayNext : null;
  } catch {
    return null;
  }
};

const savePrefs = (patch) => {
  try {
    localStorage.setItem(PREFS_KEY, JSON.stringify({ ...loadPrefs(), ...patch }));
  } catch { /* quota */ }
};

export const MAX_VOLUME = 200; // 200% — headroom for quiet recordings via Web Audio GainNode

export const PlayerProvider = ({ children }) => {
  const prefs = useRef(loadPrefs()).current;
  const audioRef = useRef(null);
  const currentRef = useRef(null);
  const queueRef = useRef([]);
  const rateRef = useRef(prefs.rate || 1);
  // Auto-next: Settings page value wins, otherwise player pref, otherwise ON.
  const _initialAutoNext = (() => {
    const fromSettings = loadAutoNextFromSettings();
    if (fromSettings !== null) return fromSettings;
    return prefs.autoNext !== false;
  })();
  const autoNextRef = useRef(_initialAutoNext);
  const resumedRef = useRef(false);
  const triedAltRef = useRef(false);
  // Web Audio graph for volume boost above 100% (element.volume caps at 1.0)
  const audioCtxRef = useRef(null);
  const gainNodeRef = useRef(null);
  const sourceNodeRef = useRef(null);
  const gainReadyRef = useRef(false);

  const [current, setCurrent] = useState(null);
  const [queue, setQueue] = useState([]);
  const [isPlaying, setIsPlaying] = useState(false);
  const [buffering, setBuffering] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(prefs.volume ?? 70);
  const [muted, setMuted] = useState(false);
  const [rate, setRate] = useState(prefs.rate || 1);
  const [autoNext, setAutoNext] = useState(_initialAutoNext);
  const [audioError, setAudioError] = useState(false);
  // Skip-silence state — set when the player auto-seeks past a silent
  // intro. UI shows a "Start from beginning" pill that clears this.
  const [silenceSkipped, setSilenceSkipped] = useState(null);

  // Stall/error recovery — last position we know played, and how many
  // automatic recoveries we've done for the current recording.
  const lastProgressRef = useRef({ time: 0, at: Date.now() });
  const recoveryRef = useRef({ attempts: 0, inFlight: false, startedAt: 0, lastAt: 0, finish: null });
  const isPlayingRef = useRef(false);
  const userPauseRef = useRef(false); // true from an explicit pause until the next play
  const playIntentRef = useRef(false); // listener wants audio playing (no explicit pause since)
  const localRef = useRef({ id: null, timer: null, started: false });
  const resumeTimerRef = useRef(null);
  // Whole-recording local copy: idle | loading | ready | offline | failed
  const [localBuffer, setLocalBuffer] = useState({ status: 'idle', progress: 0 });

  const reportDiagnostic = useCallback((event, extra = {}) => {
    const el = audioRef.current;
    const rec = currentRef.current;
    if (!el || !rec) return;
    const body = {
      drive_file_id: rec.drive_file_id || '',
      event,
      sid: (() => { try { return sessionStorage.getItem('live_sid') || ''; } catch { return ''; } })(),
      position: Number(lastProgressRef.current.time || el.currentTime || 0),
      duration: Number(el.duration || 0),
      ready_state: el.readyState,
      network_state: el.networkState,
      error_code: el.error ? el.error.code : null,
      error_message: (el.error && el.error.message) || '',
      visibility: typeof document !== 'undefined' ? document.visibilityState : '',
      online: typeof navigator !== 'undefined' ? navigator.onLine : null,
      attempt: recoveryRef.current.attempts,
      src: (el.currentSrc || '').slice(0, 300),
      ...extra,
    };
    try {
      fetch(`${BACKEND_URL}/api/player/diagnostics`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        keepalive: true,
      }).catch(() => {});
    } catch { /* ignore */ }
  }, []);

  // Replace the element's source and pick up at the same position.
  // playMode 'active' → keep playing only if the element is playing right now;
  // 'intent' → resume whenever the listener never explicitly paused.
  const swapSource = useCallback((url, { reason, playMode, event }) => {
    const el = audioRef.current;
    const rec = currentRef.current;
    const r = recoveryRef.current;
    if (!el || !rec || r.inFlight) return false;
    const pos = Math.max(0, lastProgressRef.current.time || el.currentTime || 0);
    const wasPlaying = playIntentRef.current && (playMode === 'intent' || !el.paused);
    r.inFlight = true;
    r.startedAt = Date.now();
    function finish() {
      r.inFlight = false;
      r.finish = null;
      el.removeEventListener('loadedmetadata', onReady);
      el.removeEventListener('error', onFail);
    }
    function onReady() {
      finish();
      try { el.currentTime = Math.min(pos, Math.max(0, (el.duration || pos) - 0.5)); } catch { /* ignore */ }
      el.playbackRate = rateRef.current;
      lastProgressRef.current = { time: pos, at: Date.now() };
      if (wasPlaying) el.play().catch(() => reportDiagnostic('resume_rejected', { reason }));
      reportDiagnostic(event, { reason, position: pos });
    }
    function onFail() { finish(); }
    r.finish = finish;
    el.addEventListener('loadedmetadata', onReady);
    el.addEventListener('error', onFail);
    setBuffering(true);
    el.src = url;
    el.load();
    return true;
  }, [reportDiagnostic]);

  const adoptLocal = useCallback((entry, reason, playMode) => {
    const el = audioRef.current;
    if (!el || !entry || isBlobSrc(el)) return false;
    return swapSource(entry.url, { reason, playMode, event: 'local_adopted' });
  }, [swapSource]);

  // Keep the live stream first in line for bandwidth: the background full-file
  // copy only pulls bytes while playback is on the local blob, user-paused, or
  // comfortably buffered ahead. When the live buffer dips the download backs
  // off and hands the whole connection back to playback.
  const waitForSpareBandwidth = useCallback(() => new Promise((resolve) => {
    const check = () => {
      const el = audioRef.current;
      if (!el) return resolve();
      if (isBlobSrc(el) || (el.paused && userPauseRef.current)) return resolve();
      if (bufferedAhead(el) >= DOWNLOAD_MIN_AHEAD) return resolve();
      setTimeout(check, 1000);
    };
    check();
  }), []);

  // Up-next copy only while the app is on screen: background downloads made
  // Android kill the locked-phone WebView (→ "resets every few minutes").
  const prefetchNext = useCallback(() => {
    if (document.visibilityState === 'hidden' || !autoNextRef.current) return;
    const next = queueRef.current[0];
    if (!next?.drive_file_id) return;
    prefetchLocalAudio(next.drive_file_id, `${BACKEND_URL}/api/stream/${next.drive_file_id}`, undefined, waitForSpareBandwidth).catch(() => {});
  }, [waitForSpareBandwidth]);

  // Pull the whole shiur onto the phone so the rest of playback needs no
  // network (phones cut background network → the "stops every ~6 minutes" reports).
  const startLocalPrefetch = useCallback(() => {
    const rec = currentRef.current;
    const el = audioRef.current;
    const L = localRef.current;
    if (!rec || !el || !rec.drive_file_id || !rec.stream_url) return;
    if (L.id !== rec.drive_file_id || L.started || isBlobSrc(el)) return;
    L.started = true;
    clearTimeout(L.timer);
    L.timer = null;
    const id = rec.drive_file_id;
    const t0 = Date.now();
    let lastPct = -1;
    setLocalBuffer({ status: 'loading', progress: 0 });
    prefetchLocalAudio(id, rec.stream_url, (got, total) => {
      if (currentRef.current?.drive_file_id !== id || !total) return;
      const pct = Math.min(99, Math.floor((got / total) * 100));
      if (pct !== lastPct) {
        lastPct = pct;
        setLocalBuffer({ status: 'loading', progress: pct });
      }
    }, waitForSpareBandwidth).then((entry) => {
      if (currentRef.current?.drive_file_id !== id) return;
      setLocalBuffer({ status: 'ready', progress: 100 });
      reportDiagnostic('local_ready', { reason: `${Math.round((Date.now() - t0) / 1000)}s ${(entry.size / 1048576).toFixed(1)}MB` });
      const a = audioRef.current;
      // Visible: switch now. Locked/background: switch once the network buffer
      // runs low (watchdog) or on the first hiccup — never disturb a healthy stream.
      if (a && (document.visibilityState !== 'hidden' || bufferedAhead(a) < 30)) adoptLocal(entry, 'ready', 'active');
      prefetchNext();
    }).catch(() => {
      if (currentRef.current?.drive_file_id === id) setLocalBuffer({ status: 'failed', progress: 0 });
      if (localRef.current.id === id) localRef.current.started = false;
    });
  }, [adoptLocal, reportDiagnostic, prefetchNext, waitForSpareBandwidth]);

  // Mid-listen failure: prefer the local copy (no network needed); otherwise
  // reload the stream — 4 quick tries, then every 15 s, banner after 20 tries
  // but never stop trying while the listener still wants to hear it.
  const recoverPlayback = useCallback((reason) => {
    const el = audioRef.current;
    const rec = currentRef.current;
    const r = recoveryRef.current;
    if (!el || !rec || r.inFlight || isBlobSrc(el)) return;
    const now = Date.now();
    const gap = r.attempts >= 20 ? 30000 : r.attempts >= 4 ? 15000 : 0;
    if (gap && now - r.lastAt < gap) return;
    reportDiagnostic(reason === 'error' ? 'error' : 'stall', { reason });
    const local = getLocalAudio(rec.drive_file_id);
    if (local) {
      adoptLocal(local, reason, 'intent');
      return;
    }
    startLocalPrefetch();
    r.attempts += 1;
    r.lastAt = now;
    if (r.attempts === 20) {
      reportDiagnostic('recovery_failed', { reason });
      setAudioError(true);
    }
    const bust = new URL(rec.stream_url, window.location.origin);
    bust.searchParams.set('r', String(now));
    swapSource(bust.toString(), { reason, playMode: 'intent', event: 'recovered' });
  }, [adoptLocal, startLocalPrefetch, swapSource, reportDiagnostic]);

  const playRecording = useCallback((recording, upNext = []) => {
    if (!recording) return;
    const rec = { ...recording };
    // Always stream through the backend proxy (hides the Drive API key).
    if (rec.drive_file_id) {
      rec.stream_url = `${BACKEND_URL}/api/stream/${rec.drive_file_id}`;
    } else if (rec.stream_url && rec.stream_url.startsWith('/api')) {
      rec.stream_url = `${BACKEND_URL}${rec.stream_url}`;
    }
    const q = (upNext || []).filter((r) => r.drive_file_id !== rec.drive_file_id);
    queueRef.current = q;
    setQueue(q);
    if (currentRef.current && currentRef.current.drive_file_id === rec.drive_file_id) return;
    currentRef.current = rec;
    setCurrent(rec);
    window.__vaadPlayerActive = true;
    resumedRef.current = false;
    triedAltRef.current = false;
    playIntentRef.current = false;
    clearTimeout(resumeTimerRef.current);
    lastProgressRef.current = { time: 0, at: Date.now() };
    if (recoveryRef.current.finish) recoveryRef.current.finish();
    recoveryRef.current = { attempts: 0, inFlight: false, startedAt: 0, lastAt: 0, finish: null };
    clearTimeout(localRef.current.timer);
    localRef.current = { id: rec.drive_file_id, timer: null, started: false };
    retainLocalAudio([rec.drive_file_id, q[0]?.drive_file_id]);
    const local = getLocalAudio(rec.drive_file_id);
    setLocalBuffer(local ? { status: 'ready', progress: 100 } : { status: 'idle', progress: 0 });
    setAudioError(false);
    setSilenceSkipped(null);
    setCurrentTime(0);
    setDuration(0);
    const el = audioRef.current;
    if (!el) return;

    // Prefer a copy we already hold: the in-memory local copy (pre-buffered
    // as "up next") or an offline download in IndexedDB. Both play with no
    // network at all. Falls back to the streaming URL otherwise.
    (async () => {
      let src = local ? local.url : rec.stream_url;
      let offlineUrl = null;
      if (!local) {
        try {
          offlineUrl = await getOfflineAudioUrl(rec.drive_file_id);
        } catch { /* ignore */ }
        if (offlineUrl) {
          src = offlineUrl;
          setLocalBuffer({ status: 'offline', progress: 100 });
        }
      }
      if (currentRef.current?.drive_file_id !== rec.drive_file_id) {
        // Another recording started while we were resolving offline — bail out.
        if (offlineUrl) URL.revokeObjectURL(offlineUrl);
        return;
      }
      el.src = src;
      el.playbackRate = rateRef.current;
      el.play().catch(() => setIsPlaying(false));
    })();

    incrementPlayCount(rec.drive_file_id).catch(() => {});
    trackRecordingVisit(rec);
  }, []);

  const togglePlay = useCallback(() => {
    const el = audioRef.current;
    if (!el || !currentRef.current) return;
    if (el.paused) el.play().catch(() => {});
    else { userPauseRef.current = true; playIntentRef.current = false; el.pause(); }
  }, []);

  // Explicit "start playing" (no toggle). Used by TranscriptPanel so
  // clicking a word both seeks AND resumes if the audio was paused.
  const play = useCallback(() => {
    const el = audioRef.current;
    if (!el || !currentRef.current) return;
    if (el.paused) el.play().catch(() => {});
  }, []);

  const skip = useCallback((seconds) => {
    const el = audioRef.current;
    if (!el || !Number.isFinite(el.duration) || el.duration <= 0) {
      // Feb-2026 (user "flaky skip-ahead" bug): mobile Safari drops the
      // seek when the media element hasn't started buffering yet.
      // Retry once after 250 ms so the tap doesn't feel dead.
      if (el && !el._skipRetried) {
        el._skipRetried = true;
        setTimeout(() => {
          delete el._skipRetried;
          if (Number.isFinite(el.duration) && el.duration > 0) {
            const target = Math.max(0, Math.min(el.duration, (el.currentTime || 0) + seconds));
            el.currentTime = target;
            setCurrentTime(target);
            if (!el.paused) Promise.resolve(el.play()).catch(() => {});
          }
        }, 250);
      }
      return;
    }
    const target = Math.max(0, Math.min(el.duration, (el.currentTime || 0) + seconds));
    // Optimistically push UI to the new position so the tap always feels
    // instant, even if the browser needs a moment to actually seek.
    setCurrentTime(target);
    try {
      el.currentTime = target;
    } catch { /* Safari sometimes throws on unbuffered seek — swallow */ }
    // If the audio has NOT reached HAVE_METADATA yet (readyState < 1),
    // wait for the `loadedmetadata` event and re-apply the seek. Fixes
    // the "first tap does nothing" bug on cold-start mobile.
    if (el.readyState < 1) {
      const onReady = () => {
        el.removeEventListener('loadedmetadata', onReady);
        try { el.currentTime = target; } catch { /* ignore */ }
        setCurrentTime(target);
        if (!el.paused) Promise.resolve(el.play()).catch(() => {});
      };
      el.addEventListener('loadedmetadata', onReady, { once: true });
    }
    if (!el.paused) {
      // Safari resets to 0 on some seeks unless we re-invoke play().
      Promise.resolve(el.play()).catch(() => {});
    }
  }, []);

  const seekTo = useCallback((seconds) => {
    const el = audioRef.current;
    if (!el) return;
    const target = Math.max(0, seconds || 0);
    // Feb-2026 (Transcripts deep-link): audio may still be loading when
    // TranscriptPanel fires the initial seek. If duration is unknown or
    // the element hasn't reached HAVE_METADATA yet, defer the seek until
    // 'loadedmetadata' fires. Optimistically update the UI so the
    // scrubber feels instant.
    if (!el.duration || el.readyState < 1) {
      setCurrentTime(target);
      const onReady = () => {
        el.removeEventListener('loadedmetadata', onReady);
        const clamped = Math.max(0, Math.min(el.duration || target, target));
        try { el.currentTime = clamped; } catch { /* ignore */ }
        setCurrentTime(clamped);
      };
      el.addEventListener('loadedmetadata', onReady, { once: true });
      return;
    }
    el.currentTime = Math.max(0, Math.min(el.duration, target));
    setCurrentTime(el.currentTime);
  }, []);

  const seekToPercent = useCallback((pct) => {
    const el = audioRef.current;
    if (!el || !el.duration) return;
    el.currentTime = (pct / 100) * el.duration;
    setCurrentTime(el.currentTime);
  }, []);

  const playNext = useCallback(() => {
    if (queueRef.current.length === 0) return;
    const [next, ...rest] = queueRef.current;
    playRecording(next, rest);
  }, [playRecording]);

  const restart = useCallback(() => {
    const el = audioRef.current;
    if (el) el.currentTime = 0;
  }, []);

  // Single-tap → restart current recording. Second tap within 2 seconds
  // → play the previous recording from history (Feb-2026 user request).
  // A ref-based armed-window sidesteps re-render lag so the second tap
  // is always caught within the exact window the user perceives.
  const restartArmedRef = React.useRef(0);
  const restartOrPrevious = useCallback(() => {
    const now = Date.now();
    const withinDoubleTapWindow = now - (restartArmedRef.current || 0) < 2000;
    if (withinDoubleTapWindow) {
      restartArmedRef.current = 0;
      try {
        // History is written by trackRecordingVisit (utils/history.js)
        // under `vaad_history_recordings_v1` — the writer already runs
        // on every playRecording call, so this list is always fresh.
        const raw = localStorage.getItem('vaad_history_recordings_v1');
        const visits = raw ? JSON.parse(raw) : [];
        const currentId = currentRef.current?.drive_file_id;
        // Skip the current recording — find the most recent DIFFERENT one.
        const prev = (visits || []).find((v) => v && v.drive_file_id && v.drive_file_id !== currentId);
        if (prev) {
          playRecording(prev, []);
          return;
        }
      } catch { /* fallthrough */ }
      // Nothing in history — no-op (still armed).
      return;
    }
    // First tap → restart AND arm the double-tap window.
    restartArmedRef.current = now;
    const el = audioRef.current;
    if (el) {
      el.currentTime = 0;
      setCurrentTime(0);
    }
  }, [playRecording]);

  const changeRate = useCallback((r) => {
    rateRef.current = r;
    setRate(r);
    savePrefs({ rate: r });
    const el = audioRef.current;
    if (!el) return;
    // iOS Safari will silently pause the media when playbackRate is
    // reassigned while a playback command is still in flight. Track the
    // pre-change playing state so we can resume immediately after.
    const wasPlaying = !el.paused;
    // Preserve pitch when changing speed — without this, some browsers
    // (Firefox, older Safari) either stutter or produce chipmunk audio.
    // Each vendor property is guarded independently: iOS throws on the
    // non-prefixed `preservesPitch` on some versions, which would
    // previously skip the actual `playbackRate` write below and leave
    // audio stuck at 1x.
    try { el.preservesPitch = true; } catch { /* not supported */ }
    try { el.mozPreservesPitch = true; } catch { /* not supported */ }
    try { el.webkitPreservesPitch = true; } catch { /* not supported */ }
    // Setting playbackRate while the media is still buffering can cause
    // Safari to silently pause. Defer until we have at least current data.
    const applyRate = () => {
      try { el.playbackRate = r; } catch { /* ignore */ }
      // If we were playing before the change, force a resume — iOS
      // otherwise leaves the element paused even though `paused`
      // reads `false`, which surfaces as "audio dies when you change
      // the speed" on phones.
      if (wasPlaying && el.paused) {
        el.play().catch(() => { /* ignore autoplay policy */ });
      }
    };
    if (el.readyState >= 2) {
      applyRate();
    } else {
      const once = () => { applyRate(); el.removeEventListener('canplay', once); };
      el.addEventListener('canplay', once);
    }
  }, []);

  const cycleRate = useCallback(() => {
    const idx = SPEED_OPTIONS.indexOf(rateRef.current);
    const next = SPEED_OPTIONS[(idx + 1) % SPEED_OPTIONS.length];
    changeRate(next);
  }, [changeRate]);

  // Lazily set up a Web Audio graph so we can boost quiet recordings above 100%.
  // Element.volume caps at 1.0 — this GainNode adds up to 2x headroom.
  const ensureGainNode = useCallback(() => {
    if (gainReadyRef.current) return true;
    const el = audioRef.current;
    if (!el) return false;
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return false;
      const ctx = new Ctx();
      const src = ctx.createMediaElementSource(el);
      const gain = ctx.createGain();
      gain.gain.value = 1;
      src.connect(gain);
      gain.connect(ctx.destination);
      audioCtxRef.current = ctx;
      sourceNodeRef.current = src;
      gainNodeRef.current = gain;
      gainReadyRef.current = true;
      return true;
    } catch (err) {
      // MediaElementSource fails for cross-origin media without CORS — fall back silently.
      // In that case we cap at 100% (element.volume max).
      gainReadyRef.current = false;
      return false;
    }
  }, []);

  const applyVolume = useCallback((vol) => {
    const el = audioRef.current;
    if (!el) return;
    if (vol <= 100) {
      el.volume = vol / 100;
      if (gainNodeRef.current) gainNodeRef.current.gain.value = 1;
      return;
    }
    // Boost path — try to route through the gain node.
    const ok = ensureGainNode();
    if (ok && gainNodeRef.current && audioCtxRef.current) {
      if (audioCtxRef.current.state === 'suspended') {
        audioCtxRef.current.resume().catch(() => {});
      }
      el.volume = 1;
      gainNodeRef.current.gain.value = vol / 100; // e.g. 1.5 = +50%, 2.0 = +100%
    } else {
      // No Web Audio available — clamp to 100.
      el.volume = 1;
    }
  }, [ensureGainNode]);

  const changeVolume = useCallback((v) => {
    const vol = Math.max(0, Math.min(MAX_VOLUME, v));
    setVolume(vol);
    savePrefs({ volume: vol });
    applyVolume(vol);
    if (vol > 0) {
      setMuted(false);
      if (audioRef.current) audioRef.current.muted = false;
    }
  }, [applyVolume]);

  const toggleMute = useCallback(() => {
    setMuted((m) => {
      const next = !m;
      if (audioRef.current) audioRef.current.muted = next;
      return next;
    });
  }, []);

  const toggleAutoNext = useCallback(() => {
    setAutoNext((a) => {
      autoNextRef.current = !a;
      savePrefs({ autoNext: !a });
      return !a;
    });
  }, []);

  const closePlayer = useCallback(() => {
    const el = audioRef.current;
    if (el) {
      userPauseRef.current = true;
      playIntentRef.current = false;
      el.pause();
      el.removeAttribute('src');
      el.load();
    }
    clearTimeout(localRef.current.timer);
    clearTimeout(resumeTimerRef.current);
    localRef.current = { id: null, timer: null, started: false };
    retainLocalAudio([]);
    setLocalBuffer({ status: 'idle', progress: 0 });
    currentRef.current = null;
    queueRef.current = [];
    setCurrent(null);
    setQueue([]);
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);
    window.__vaadPlayerActive = false;
    window.dispatchEvent(new Event('vaad:audio-stopped'));
  }, []);

  const handleTimeUpdate = useCallback(() => {
    const el = audioRef.current;
    if (!el) return;
    setCurrentTime(el.currentTime || 0);
    // A src reset snaps currentTime to 0 without a `seeking` event — don't
    // let that wipe the position we'd resume from.
    const t = el.currentTime || 0;
    if (t !== lastProgressRef.current.time && !(t === 0 && lastProgressRef.current.time > 1)) {
      lastProgressRef.current = { time: t, at: Date.now() };
    }
    if (currentRef.current && el.duration) {
      saveResumePosition(currentRef.current, el.currentTime, el.duration);
    }
    if ('mediaSession' in navigator && el.duration) {
      try {
        navigator.mediaSession.setPositionState({
          duration: el.duration,
          playbackRate: el.playbackRate,
          position: Math.min(el.currentTime, el.duration),
        });
      } catch { /* unsupported */ }
    }
  }, []);

  const handleLoadedMetadata = useCallback(() => {
    const el = audioRef.current;
    if (!el) return;
    setDuration(el.duration || 0);
    if (!resumedRef.current && currentRef.current) {
      const entry = getResumePosition(currentRef.current.drive_file_id);
      if (entry && entry.position && el.duration) {
        // User was mid-listen — resume where they left off.
        el.currentTime = Math.min(entry.position, el.duration - 3);
        // Surface a small confirmation so the listener knows the app
        // remembered the spot (Feb 2026 request). Uses the formatted
        // seconds so "Resumed at 12:34" reads naturally.
        try {
          const t = formatTime(el.currentTime);
          toast.success(`Resumed at ${t}`, {
            duration: 3500,
            action: {
              label: 'Start over',
              onClick: () => {
                el.currentTime = 0;
                setCurrentTime(0);
              },
            },
          });
        } catch { /* toast unavailable */ }
      } else {
        // Fresh play. If we detected a silent intro (>2 s) for this
        // recording, auto-seek past it and let the UI show a pill so
        // the listener can jump back to 0:00 if they want the intro.
        const skip = Number(currentRef.current.silence_offset_sec || 0);
        if (skip >= 2 && el.duration && skip < el.duration - 10) {
          el.currentTime = skip;
          setSilenceSkipped({ offset: skip });
        }
      }
      resumedRef.current = true;
    }
  }, []);

  // Called by the "Play from beginning" pill in the player UI.
  const playFromBeginning = useCallback(() => {
    const el = audioRef.current;
    if (!el) return;
    el.currentTime = 0;
    setCurrentTime(0);
    setSilenceSkipped(null);
    if (el.paused) el.play().catch(() => {});
  }, []);

  const handleEnded = useCallback(() => {
    const el = audioRef.current;
    if (currentRef.current && el) {
      saveResumePosition(currentRef.current, el.duration || 0, el.duration || 0);
    }
    if (autoNextRef.current && queueRef.current.length > 0) {
      const [next, ...rest] = queueRef.current;
      playRecording(next, rest);
    } else {
      playIntentRef.current = false;
      setIsPlaying(false);
    }
  }, [playRecording]);

  const handleError = useCallback(() => {
    const el = audioRef.current;
    const rec = currentRef.current;
    if (!el || !rec) return;
    if (recoveryRef.current.inFlight) return; // recoverPlayback's own listener handles this one
    // Mid-listen failure (network dropped, phone slept, CDN connection
    // closed): reload the stream and pick up where we were instead of
    // restarting from 0:00 or showing the "unavailable" banner.
    if ((lastProgressRef.current.time || 0) > 1) {
      const delay = 1000 * Math.min(5, Math.max(1, recoveryRef.current.attempts));
      setTimeout(() => { if (currentRef.current === rec) recoverPlayback('error'); }, delay);
      return;
    }
    if (!triedAltRef.current && rec.drive_file_id) {
      // Feb-2026 (user reported "Audio streaming unavailable" on a Drive-only
      // file that streamed fine seconds later): before we blame the file,
      // check whether the backend responded with a *transient* 503 (our own
      // "Drive stream temporarily unavailable — try again in a moment" hint
      // from stream_proxy.py). Retry twice, respecting the server's
      // Retry-After header — most transient Drive throttles clear inside
      // 5 s. Only after both retries fail do we surface the banner.
      triedAltRef.current = true;
      const base = rec.stream_url || `/api/stream/${rec.drive_file_id}`;
      const url = new URL(base, window.location.origin);

      const probe = async () => {
        for (let attempt = 0; attempt < 2; attempt += 1) {
          try {
            const r = await fetch(url.pathname, {
              method: 'GET',
              headers: { Range: 'bytes=0-0' },
              cache: 'no-store',
              credentials: 'omit',
            });
            if (r.ok || r.status === 206) {
              // Cache is warm now — swap the src to a cache-busted URL and
              // let the <audio> element start playing.
              const bust = new URL(base, window.location.origin);
              bust.searchParams.set('r', Date.now().toString());
              el.src = bust.pathname + bust.search;
              el.play().catch(() => {});
              return;
            }
            if (r.status !== 503) break; // hard failure — no point retrying
            const wait = parseInt(r.headers.get('retry-after') || '3', 10);
            await new Promise((res) => setTimeout(res, Math.max(500, wait * 1000)));
          } catch {
            await new Promise((res) => setTimeout(res, 1500));
          }
        }
        setAudioError(true);
        setBuffering(false);
      };
      probe();
    } else {
      reportDiagnostic('error', { reason: 'initial_load' });
      setAudioError(true);
      setBuffering(false);
    }
  }, [recoverPlayback, reportDiagnostic]);

  // Watchdog (every 5 s):
  //  • a source swap that never produced metadata within 20 s → retry;
  //  • local copy ready while still streaming → switch (at once when visible,
  //    when the network buffer drops under 30 s in the background);
  //  • playing but no progress for 20 s → recover at the same position;
  //  • system-paused, starved for 20 s → recover (listener never paused).
  useEffect(() => {
    const t = setInterval(() => {
      const el = audioRef.current;
      const rec = currentRef.current;
      if (!el || !rec) return;
      const r = recoveryRef.current;
      const now = Date.now();
      if (r.inFlight) {
        if (now - r.startedAt > 20000) {
          if (r.finish) r.finish();
          recoverPlayback('timeout');
        }
        return;
      }
      if (!playIntentRef.current || el.ended || el.seeking) return;
      if (!isBlobSrc(el)) {
        const local = getLocalAudio(rec.drive_file_id);
        if (local && !el.paused && (document.visibilityState !== 'hidden' || bufferedAhead(el) < 30)) {
          adoptLocal(local, 'buffer_low', 'active');
          return;
        }
      }
      if (el.paused) {
        if (now - lastProgressRef.current.at > 20000 && bufferedAhead(el) < 3) recoverPlayback('paused_starved');
        return;
      }
      const limit = lastProgressRef.current.time > 0 ? 20000 : 45000;
      if (now - lastProgressRef.current.at > limit) recoverPlayback('stall');
    }, 5000);
    return () => clearInterval(t);
  }, [recoverPlayback, adoptLocal]);

  // Locked phone → start the local copy right away; back in the app → the
  // safest moment to switch onto a finished copy; network back → recover.
  useEffect(() => {
    const onVisibility = () => {
      const el = audioRef.current;
      const rec = currentRef.current;
      if (!el || !rec) return;
      if (document.visibilityState === 'hidden') {
        if (playIntentRef.current) startLocalPrefetch();
        return;
      }
      const local = getLocalAudio(rec.drive_file_id);
      if (local && !isBlobSrc(el) && !recoveryRef.current.inFlight) adoptLocal(local, 'visible', 'active');
      if (local) prefetchNext();
    };
    const onOnline = () => {
      if (playIntentRef.current && Date.now() - lastProgressRef.current.at > 5000) recoverPlayback('online');
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('online', onOnline);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('online', onOnline);
    };
  }, [startLocalPrefetch, adoptLocal, recoverPlayback, prefetchNext]);

  // Buffer trouble mid-listen: switch to the local copy if we have it,
  // otherwise start building it now.
  const handleTrouble = useCallback(() => {
    const el = audioRef.current;
    const rec = currentRef.current;
    if (!el || !rec || isBlobSrc(el) || recoveryRef.current.inFlight || lastProgressRef.current.time <= 1) return;
    const local = getLocalAudio(rec.drive_file_id);
    if (local) adoptLocal(local, 'waiting', 'active');
    else if (playIntentRef.current) startLocalPrefetch();
  }, [adoptLocal, startLocalPrefetch]);

  // Let the service-worker updater know whether reloading now would cut
  // off a listener (see index.js controllerchange handling).
  const handlePlay = useCallback(() => {
    isPlayingRef.current = true;
    userPauseRef.current = false;
    playIntentRef.current = true;
    lastProgressRef.current = { ...lastProgressRef.current, at: Date.now() };
    window.__vaadAudioPlaying = true;
    setIsPlaying(true);
    const L = localRef.current;
    if (L.id && !L.started && !L.timer) {
      L.timer = setTimeout(() => {
        L.timer = null;
        const el = audioRef.current;
        if (localRef.current === L && el && !el.paused && playIntentRef.current) startLocalPrefetch();
      }, LOCAL_START_DELAY_MS);
    }
  }, [startLocalPrefetch]);

  const handlePause = useCallback(() => {
    const el = audioRef.current;
    isPlayingRef.current = false;
    setIsPlaying(false);
    if (recoveryRef.current.inFlight) return; // src swap mid-recovery, not a real stop
    window.__vaadAudioPlaying = false;
    const atEnd = !el || el.ended || (el.duration && el.currentTime >= el.duration - 0.5);
    if (!userPauseRef.current && !atEnd && el && el.currentTime > 1) {
      reportDiagnostic('unexpected_pause');
      // iPhones pause (no error) when the background stream starves. If the
      // element really ran dry, bring it back — from the local copy when we
      // have one. A pause with audio still buffered ahead (call, headphones
      // unplugged, system) is left alone.
      const rec = currentRef.current;
      clearTimeout(resumeTimerRef.current);
      resumeTimerRef.current = setTimeout(() => {
        const a = audioRef.current;
        if (!a || currentRef.current !== rec || !playIntentRef.current || !a.paused) return;
        if (recoveryRef.current.inFlight || bufferedAhead(a) >= 3) return;
        const local = getLocalAudio(rec.drive_file_id);
        if (local && !isBlobSrc(a)) {
          adoptLocal(local, 'pause', 'intent');
        } else {
          reportDiagnostic('auto_resume', { reason: 'play' });
          a.play().catch(() => reportDiagnostic('resume_rejected', { reason: 'pause' }));
        }
      }, 1500);
    }
    window.dispatchEvent(new Event('vaad:audio-stopped'));
  }, [reportDiagnostic, adoptLocal]);

  // Media Session metadata + action handlers
  useEffect(() => {
    if (!('mediaSession' in navigator)) return;
    if (!current) {
      navigator.mediaSession.metadata = null;
      return;
    }
    const art = current.cover_image && current.cover_image.startsWith('http')
      ? current.cover_image
      : `${window.location.origin}/vaad-otzar-logo.jpg`;
    navigator.mediaSession.metadata = new window.MediaMetadata({
      title: current.title || 'Shiur',
      artist: current.speaker || 'The Vaad Otzar',
      album: 'The Vaad Otzar',
      artwork: [{ src: art, sizes: '512x512', type: 'image/jpeg' }],
    });
  }, [current]);

  useEffect(() => {
    if (!('mediaSession' in navigator)) return undefined;
    const ms = navigator.mediaSession;
    const safeSet = (action, handler) => {
      try { ms.setActionHandler(action, handler); } catch { /* unsupported */ }
    };
    safeSet('play', () => audioRef.current?.play().catch(() => {}));
    safeSet('pause', () => { userPauseRef.current = true; playIntentRef.current = false; audioRef.current?.pause(); });
    safeSet('seekbackward', () => skip(-15));
    safeSet('seekforward', () => skip(15));
    safeSet('nexttrack', () => playNext());
    safeSet('previoustrack', () => restart());
    safeSet('seekto', (d) => { if (d.seekTime != null) seekTo(d.seekTime); });
    return () => {
      ['play', 'pause', 'seekbackward', 'seekforward', 'nexttrack', 'previoustrack', 'seekto']
        .forEach((a) => safeSet(a, null));
    };
  }, [skip, playNext, restart, seekTo]);

  // Keyboard shortcuts
  useEffect(() => {
    const onKey = (e) => {
      if (!currentRef.current) return;
      const t = e.target;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable)) return;
      if (e.code === 'Space') {
        e.preventDefault();
        togglePlay();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        skip(-15);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        skip(15);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        changeVolume(volume + 10);
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        changeVolume(volume - 10);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [togglePlay, skip, changeVolume, volume]);

  // Apply initial volume once audio element mounts
  useEffect(() => {
    applyVolume(prefs.volume ?? 70);
  }, [applyVolume, prefs.volume]);

  const value = {
    current,
    queue,
    isPlaying,
    buffering,
    currentTime,
    duration,
    progress: duration ? (currentTime / duration) * 100 : 0,
    volume,
    muted,
    rate,
    autoNext,
    audioError,
    playRecording,
    togglePlay,
    play,
    skip,
    seekTo,
    seekToPercent,
    playNext,
    restart,
    restartOrPrevious,
    changeRate,
    cycleRate,
    changeVolume,
    toggleMute,
    toggleAutoNext,
    closePlayer,
    silenceSkipped,
    playFromBeginning,
    localBuffer,
  };

  return (
    <PlayerContext.Provider value={value}>
      {children}
      <audio
        ref={audioRef}
        crossOrigin="anonymous"
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={handleEnded}
        onError={handleError}
        onPlay={handlePlay}
        onPause={handlePause}
        onSeeking={() => { const el = audioRef.current; if (el) lastProgressRef.current = { time: el.currentTime || 0, at: Date.now() }; }}
        onWaiting={() => { setBuffering(true); handleTrouble(); }}
        onStalled={handleTrouble}
        onPlaying={() => { setBuffering(false); setAudioError(false); recoveryRef.current.attempts = 0; }}
        onCanPlay={() => setBuffering(false)}
        preload="auto"
        playsInline
        x-webkit-airplay="allow"
      />
    </PlayerContext.Provider>
  );
};
